import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLogout, useSession, type AdminSession } from '../lib/session';
import { useMyWorkCount } from '../lib/queries';
import { LanguagePicker, Logo, Pill } from './ui';
import { useI18n } from '../lib/i18n';
import type { StringKey } from '../lib/i18n';

// `exact` matters where one route is a prefix of another: /orders must not
// light up while /orders/new is open, but /customers must stay lit on a detail page.
const NAV: Array<{ to: string; label: StringKey; permission?: string; ready: boolean; exact?: boolean }> = [
  { to: '/', label: 'nav.dashboard', permission: 'reports.read', ready: true },
  { to: '/my-work', label: 'nav.myWork', permission: 'orders.work', ready: true },
  { to: '/floor', label: 'nav.floor', permission: 'orders.read', ready: true, exact: true },
  { to: '/calendar', label: 'nav.calendar', permission: 'bookings.read', ready: true },
  { to: '/customers', label: 'nav.customers', permission: 'customers.read', ready: true },
  { to: '/orders/new', label: 'nav.newOrder', permission: 'orders.create', ready: true },
  { to: '/scan', label: 'nav.scan', permission: 'qr.resolve', ready: true },
  { to: '/orders', label: 'nav.orders', permission: 'orders.read', ready: true, exact: true },
  { to: '/followups', label: 'nav.followups', permission: 'customers.read', ready: true },
  { to: '/vehicles', label: 'nav.vehicles', permission: 'vehicles.read', ready: true },
  { to: '/services', label: 'nav.services', permission: 'services.read', ready: true },
  { to: '/rewards', label: 'nav.rewards', permission: 'rewards.read', ready: true },
  
  { to: '/wallets', label: 'nav.wallets', permission: 'wallet.read', ready: true },
  { to: '/imports', label: 'nav.imports', permission: 'imports.read', ready: true },
  { to: '/reports', label: 'nav.reports', permission: 'reports.read', ready: true },
  { to: '/payroll', label: 'nav.payroll', permission: 'staff.pay', ready: true },
  { to: '/staff', label: 'nav.staff', permission: 'staff.read', ready: true },
  { to: '/audit', label: 'nav.audit', permission: 'audit.read', ready: true },
  // Exact, or the account's own /settings/password lights this up as well.
  { to: '/settings', label: 'nav.settings', permission: 'settings.read', ready: true, exact: true },
];

function Initials({ session }: { session: AdminSession }) {
  const initials = `${session.firstName[0] ?? ''}${session.lastName[0] ?? ''}`.toUpperCase();
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line bg-raised text-xs font-bold text-ink-dim">
      {initials || '?'}
    </div>
  );
}

function NavList({ session, onNavigate }: { session: AdminSession; onNavigate?: () => void }) {
  const { t } = useI18n();
  const visible = NAV.filter((item) => !item.permission || session.permissions.includes(item.permission));

  /*
   * How many jobs are waiting on this person, shown against "My work".
   *
   * A technician in a bay does not refresh a page to find out they have been
   * given a car — the number has to arrive on its own. Asked for only by the
   * people the answer can be non-zero for.
   */
  const { data: mine } = useMyWorkCount(session.permissions.includes('orders.work'));
  const waiting = mine?.waiting ?? 0;

  return (
    <nav className="flex flex-col gap-0.5 p-3">
      {visible.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.exact ?? item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex min-h-11 items-center justify-between gap-2 rounded-xl px-3.5 text-sm transition-colors ${
              isActive
                ? 'bg-brand-wash font-semibold text-brand'
                : 'text-ink-dim hover:bg-raised hover:text-ink'
            }`
          }
        >
          {t(item.label)}
          {item.to === '/my-work' && waiting > 0 && <Pill tone="warning">{waiting}</Pill>}
          {!item.ready && <Pill>{t('nav.soon')}</Pill>}
        </NavLink>
      ))}
    </nav>
  );
}

function Account({ session, onNavigate }: { session: AdminSession; onNavigate?: () => void }) {
  const { t } = useI18n();
  const logout = useLogout();
  return (
    <div className="border-t border-line p-3">
      <LanguagePicker className="px-2 pb-2" />
      <div className="flex items-center gap-3 rounded-xl px-2 py-2">
        <Initials session={session} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-medium text-ink">
            {session.firstName} {session.lastName}
          </div>
          <div className="truncate text-[0.7rem] text-ink-faint">{session.roles.join(', ')}</div>
        </div>
      </div>
      {/* Everyone has a password and may add a code, whatever their role, so
          this sits with the name rather than in the studio's settings. */}
      <NavLink
        to="/settings/password"
        onClick={onNavigate}
        className={({ isActive }) =>
          `mt-1 flex min-h-11 items-center rounded-xl px-3.5 text-sm transition-colors ${
            isActive ? 'bg-brand-wash font-semibold text-brand' : 'text-ink-faint hover:bg-raised hover:text-ink'
          }`
        }
      >
        {t('nav.security')}
      </NavLink>
      <button
        onClick={() => { onNavigate?.(); logout.mutate(); }}
        className="min-h-11 w-full rounded-xl px-3.5 text-left text-sm text-ink-faint transition-colors hover:bg-raised hover:text-ink"
      >
        {t('nav.signOut')}
      </button>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { data: session } = useSession();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setDrawerOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  if (!session) return null;

  return (
    <div className="flex min-h-dvh">
      {/*
        Sticky and exactly one screen tall. Left in the flow it scrolled away
        with the page, so on a long list the menu ended up somewhere above the
        viewport and the account block sat at the very bottom of the document.
      */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="border-b border-line px-5 py-5">
          <Logo withWordmark />
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavList session={session} />
        </div>
        <Account session={session} />
      </aside>

      {/*
        Installed on an iPhone the page runs under the status bar
        (black-translucent), so the bar's own height is added on top of the
        header's. In a browser tab the inset is zero and nothing moves.
      */}
      <header className="fixed inset-x-0 top-0 z-30 box-content flex h-[3.75rem] items-center justify-between gap-3 border-b border-line bg-surface/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur-sm lg:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label={t('nav.menu')}
          aria-expanded={drawerOpen}
          className="-ml-2 grid h-11 w-11 place-items-center rounded-xl text-ink-dim transition-colors hover:bg-raised hover:text-ink"
        >
          <svg viewBox="0 0 20 14" className="h-4 w-5" aria-hidden>
            <path d="M0 1h20M0 7h20M0 13h20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        <Logo size="sm" />
        <div className="h-11 w-11" />
      </header>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            aria-label={t('nav.menu')}
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <div className="absolute inset-y-0 left-0 flex w-[17rem] max-w-[85vw] flex-col border-r border-line bg-surface pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-line px-4 py-4">
              <Logo withWordmark size="sm" />
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label={t('nav.menu')}
                className="-mr-1 grid h-10 w-10 place-items-center rounded-xl text-ink-faint transition-colors hover:bg-raised hover:text-ink"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavList session={session} onNavigate={() => setDrawerOpen(false)} />
            </div>
            <Account session={session} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex-1 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(4.5rem+env(safe-area-inset-top))] sm:px-5 lg:px-8 lg:py-8 lg:pt-8">
          {children}
        </main>
      </div>
    </div>
  );
}
